<template>
    <div>
        <div v-if="!$store.showLoadingOverlay" class="cic-popup">
            <ul class="cic-container">
                <li class="cic-header">
                    <span class="cic-header-title">{{ $t('User SAML Attribute Values') }}</span>
                </li>
                <li class="cic-popup-body">
                    <div v-if="enabled" class="item-form">
                        <ul>
                            <li class="form-label-first">
                                {{ $t('Import') }}
                            </li>
                            <li class="inline-form-item">
                                <input id="fImport" type="file" @change="handleImport" />
                                <icon-button icon="help-icon" :title="$t('SAML_IMPORT_MAX_LINES')" />
                            </li>
                            <li style="padding-top: 20px">
                                <cic-textbox
                                    :value="searchText"
                                    :placeholder="$t('Search')"
                                    clearable
                                    @input="filter"
                                />
                            </li>
                        </ul>
                    </div>
                    <br />
                    <table id="user_saml_table" class="cic-table">
                        <thead>
                            <tr>
                                <th>{{ $t('User') }}</th>
                                <th>{{ $t('SAML attribute value') }}</th>
                                <th v-if="enabled"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="enabled">
                                <td>
                                    <cic-select
                                        :value="userId"
                                        :options="filteredOptions"
                                        :remote-method="getOptions"
                                        filterable
                                        remote
                                        @input="(val) => (userId = val)"
                                    />
                                </td>
                                <td><cic-textbox :value="samlValue" @input="(val) => (samlValue = val)" /></td>
                                <td><icon-button v-if="userId && samlValue" icon="add-icon" @click="addUser" /></td>
                            </tr>
                            <tr v-for="itemUser in rows">
                                <td>{{ itemUser.label }}</td>
                                <td>{{ itemUser.samlValue }}</td>
                                <td v-if="enabled">
                                    <icon-button icon="remove-icon" @click="removeUser(itemUser.userId)" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </li>
                <li class="cic-footer">
                    <cic-page-control
                        :page="page"
                        :results-per-page="100"
                        :total-rows="filtered.length"
                        style="float: left"
                        @change="(val) => (page = val)"
                    />
                    <cic-button id="popup_cancel" @click="$emit('blur')">
                        {{ $t('Cancel') }}
                    </cic-button>
                    <template v-if="item.users.length && (item.users.length > 1 || item.users[0].userId !== null)">
                        <a :href="link" :download="item.name + '.csv'">
                            <cic-button id="popup_export" :style="{ marginLeft: '5px' }">
                                {{ $t('Export') }}
                            </cic-button>
                        </a>
                        <cic-button id="popup_clear" :style="{ marginLeft: '5px' }" @click="handleClear">
                            {{ $t('Clear All') }}
                        </cic-button>
                    </template>
                    <cic-button v-if="enabled" id="popup_save" @click="$emit('input')">
                        {{ $t('Save') }}
                    </cic-button>
                </li>
            </ul>
        </div>
        <div v-if="!$store.showLoadingOverlay" class="cic-overlay" @click="$emit('blur')"></div>
    </div>
</template>

<script>
    import CicButton from '@/components/CicButton';
    import CicPageControl from '@/components/CicPageControl';
    import IconButton from '@/components/controls/IconButton';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import { formatUsername } from '@/feature/licenceAdmin/LicenceUtils';
    import File from '@/mixins/File';
    import { showErrorMessage } from '@/utils/Notification';

    export default {
        name: 'EditIdentityProviderUsers',
        components: {
            CicButton,
            CicPageControl,
            CicSelect,
            CicTextbox,
            IconButton,
        },
        mixins: [File],
        props: {
            enabled: {
                type: Boolean,
                default: true,
            },
            item: {
                type: Object,
                required: true,
            },
            users: {
                type: Array,
                required: true,
            },
        },
        emits: ['blur', 'clear', 'import', 'input', 'updateFieldValue'],
        data() {
            return {
                filteredOptions: [],
                initialOptions: [],
                page: 1,
                userId: null,
                samlValue: null,
                searchText: '',
            };
        },
        computed: {
            available() {
                const userIds = this.item.users.map((x) => x.userId);
                return this.initialOptions.filter((x) => !userIds.includes(x.value));
            },
            filtered() {
                if (this.searchText.trim() !== '') {
                    return this.item.users.filter(
                        (x) =>
                            x.label.toLowerCase().includes(this.searchText.toLowerCase()) ||
                            x.samlValue.toLowerCase().includes(this.searchText.toLowerCase()),
                    );
                }

                return this.item.users;
            },
            link() {
                let users = this.item.users.filter((x) => x.userId).map((x) => x.label + ',' + x.samlValue);
                let csvContent =
                    'data:text/csv;charset=utf-8,' +
                    [[this.$t('User'), this.$t('SAML attribute value')]].concat(users).join('\n');
                return encodeURI(csvContent);
            },
            rows() {
                return this.filtered.slice((this.page - 1) * 100, this.page * 100);
            },
        },
        watch: {
            item() {
                this.filteredOptions = this.available.slice(0, 100);
            },
        },
        created() {
            this.initialOptions = this.users.map((x) => ({
                value: x.userId,
                label: formatUsername(x),
            }));
            this.filteredOptions = this.available.slice(0, 100);
        },
        methods: {
            addUser() {
                const users = [
                    {
                        userId: this.userId,
                        samlValue: this.samlValue,
                        label: this.initialOptions.find((x) => x.value === this.userId).label,
                    },
                ].concat([...this.item.users]);

                this.page = 1;
                this.userId = null;
                this.samlValue = null;

                this.$emit('updateFieldValue', 'users', users);
            },

            fileOnLoad(content) {
                try {
                    let users = [];
                    let lines = this.parseCsv(content);
                    if (lines.length > 400) {
                        this.$store.hideLoadingOverlay();
                        alert(this.$t('SAML_IMPORT_MAX_LINES'));
                    } else {
                        users = this.validateContent(lines);
                    }

                    if (users.length > 0 && this.validateUsers(users)) {
                        this.$emit('import', users);
                    } else {
                        this.$store.hideLoadingOverlay();
                    }
                } catch (err) {
                    this.$store.hideLoadingOverlay();
                    showErrorMessage(this.$t('CSV_PARSE_FAILED'));
                }
            },

            filter(val) {
                this.searchText = val;
                this.page = 1;
            },

            getOptions(query) {
                this.filteredOptions = this.available
                    .filter((x) => query === '' || x.label.toLowerCase().includes(query.toLowerCase()))
                    .slice(0, 100);
            },

            handleClear() {
                if (
                    confirm(
                        this.$t(
                            'All current user SAML attribute values will be exported to a csv file and then removed. Are you sure you want to continue?',
                        ),
                    )
                ) {
                    this.$el.querySelector('#popup_export').click();
                    this.$emit('clear');
                }
            },

            handleImport(evt) {
                this.$store.setShowLoadingOverlay('Saving...');
                this.readFile(evt);
            },

            removeUser(userId) {
                let users = [...this.item.users];
                users.splice(
                    users.findIndex((x) => x.userId === userId),
                    1,
                );
                this.$emit('updateFieldValue', 'users', users);
            },

            validateContent(lines) {
                const alerts = [];
                const users = [];
                lines.map((x, index) => {
                    if (x.length !== 2) {
                        alerts.push(this.$t('Incorrect number of columns on line [x]').replace(/\[x]/g, index + 1));
                    } else if (x[0] !== this.$t('User') || x[1] !== this.$t('SAML attribute value')) {
                        let user = this.users.find(
                            (y) =>
                                x[0].trim() === `${y.firstName} ${y.lastName} (${y.username})` ||
                                x[0].trim() === y.username ||
                                x[0].trim() === y.firstName + ' ' + y.lastName,
                        );
                        if (!user) {
                            alerts.push(this.$t('User not found on line [x]').replace(/\[x]/g, index + 1));
                        } else if (users.some((y) => y.userId === user.userId)) {
                            alerts.push(this.$t('Duplicate user detected on line [x]').replace(/\[x]/g, index + 1));
                        } else if (x[1].trim() !== '' && users.some((y) => y.samlValue === x[1])) {
                            alerts.push(
                                this.$t('Duplicate SAML attribute value detected on line [x]').replace(
                                    /\[x]/g,
                                    index + 1,
                                ),
                            );
                        } else {
                            users.push({
                                userId: user.userId,
                                samlValue: x[1],
                                label: formatUsername(user),
                                index,
                            });
                        }
                    }
                });

                if (alerts.length > 0) {
                    alert(alerts.join('\r\n'));
                    return [];
                }

                return users;
            },

            validateUsers(users) {
                const userIds = users.map((x) => x.userId);
                const absent = this.item.users.filter((x) => !userIds.includes(x.userId));
                const alerts = users
                    .filter((x) => absent.some((y) => y.samlValue === x.samlValue))
                    .map((x) =>
                        this.$t('Duplicate SAML attribute value detected on line [x]').replace(/\[x]/g, x.index + 1),
                    );
                if (alerts.length > 0) {
                    alert(alerts.join('\r\n'));
                    return false;
                }

                return true;
            },
        },
    };
</script>
